/**
 * Browser tasks (AMO-155) — a browser job the helper has done once, written
 * down so a workflow's Browser task step can do it again.
 *
 * A task is personal (its owner's alone) or the organization's (any member
 * may use it; an admin manages it). Every save is a new version; a run keeps
 * the version it started with. A task the agent drafted from a chat is a
 * `draft` until its owner reviews it; only an `active` task can run.
 *
 * Wire shapes only: the store and routes live in
 * `apps/server/src/amoreg/workflows/`.
 *
 * @module browserTasks
 */
import * as Schema from "effect/Schema";

import { IsoDateTime, NonNegativeInt, TrimmedNonEmptyString } from "./baseSchemas.ts";

export const BROWSER_TASK_LIMITS = {
  nameLength: 200,
  purposeLength: 2_000,
  startUrlLength: 2_000,
  stepsLength: 10_000,
  successCheckLength: 2_000,
  producesLength: 1_000,
  domains: 20,
  domainLength: 253,
  /** Browser actions one run of a task may take. */
  actions: 40,
} as const;

/**
 * A site the task may visit: a host name, or `*.` and a host name for its
 * subdomains (`*.gov.uk`). No scheme, path or port.
 */
export const BrowserTaskDomain = TrimmedNonEmptyString.check(
  Schema.isMaxLength(BROWSER_TASK_LIMITS.domainLength),
  Schema.isPattern(/^(\*\.)?([a-z0-9]([a-z0-9-]*[a-z0-9])?\.)+[a-z]{2,}$/i),
);
export type BrowserTaskDomain = typeof BrowserTaskDomain.Type;

/** What the helper is told: where to start, what to do, and how to know it worked. */
export const BrowserTaskDefinition = Schema.Struct({
  name: TrimmedNonEmptyString.check(Schema.isMaxLength(BROWSER_TASK_LIMITS.nameLength)),
  /** When to use it, in a sentence: "This week's FCA final notices as PDFs". */
  purpose: Schema.String.check(Schema.isMaxLength(BROWSER_TASK_LIMITS.purposeLength)),
  startUrl: TrimmedNonEmptyString.check(
    Schema.isMaxLength(BROWSER_TASK_LIMITS.startUrlLength),
    Schema.isPattern(/^https:\/\//),
  ),
  /** The steps in plain words, with the page and element hints that found them. */
  steps: TrimmedNonEmptyString.check(Schema.isMaxLength(BROWSER_TASK_LIMITS.stepsLength)),
  /** What the page shows when the task worked. */
  successCheck: Schema.String.check(Schema.isMaxLength(BROWSER_TASK_LIMITS.successCheckLength)),
  /** The files it produces, if any: "One PDF per notice". */
  produces: Schema.String.check(Schema.isMaxLength(BROWSER_TASK_LIMITS.producesLength)),
  allowedDomains: Schema.Array(BrowserTaskDomain).check(
    Schema.isMinLength(1),
    Schema.isMaxLength(BROWSER_TASK_LIMITS.domains),
  ),
});
export type BrowserTaskDefinition = typeof BrowserTaskDefinition.Type;

export const BrowserTaskScope = Schema.Literals(["personal", "organization"]);
export type BrowserTaskScope = typeof BrowserTaskScope.Type;

export const BrowserTaskStatus = Schema.Literals(["draft", "active"]);
export type BrowserTaskStatus = typeof BrowserTaskStatus.Type;

export const BrowserTaskSummary = Schema.Struct({
  id: Schema.String,
  organizationId: Schema.String,
  scope: BrowserTaskScope,
  status: BrowserTaskStatus,
  version: NonNegativeInt,
  name: Schema.String,
  purpose: Schema.String,
  /** A personal task's owner; null for the organization's. */
  ownerEmail: Schema.NullOr(Schema.String),
  createdByEmail: Schema.NullOr(Schema.String),
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
  /** Whether the viewer may change or delete it. */
  canEdit: Schema.Boolean,
});
export type BrowserTaskSummary = typeof BrowserTaskSummary.Type;

export const BrowserTask = Schema.Struct({
  ...BrowserTaskSummary.fields,
  definition: BrowserTaskDefinition,
});
export type BrowserTask = typeof BrowserTask.Type;

/** The version of a task a run uses, frozen into the run when it starts. */
export const BrowserTaskSnapshot = Schema.Struct({
  id: Schema.String,
  version: NonNegativeInt,
  definition: BrowserTaskDefinition,
});
export type BrowserTaskSnapshot = typeof BrowserTaskSnapshot.Type;

export const BrowserTaskListResponse = Schema.Struct({ tasks: Schema.Array(BrowserTaskSummary) });
export type BrowserTaskListResponse = typeof BrowserTaskListResponse.Type;

export const BrowserTaskResponse = Schema.Struct({ task: BrowserTask });
export type BrowserTaskResponse = typeof BrowserTaskResponse.Type;

export const BrowserTaskCreateRequest = Schema.Struct({
  scope: BrowserTaskScope,
  definition: BrowserTaskDefinition,
  /** Absent: active. */
  status: Schema.optional(BrowserTaskStatus),
});
export type BrowserTaskCreateRequest = typeof BrowserTaskCreateRequest.Type;

/** Saving names the version it was edited from; a stale save is refused (409). */
export const BrowserTaskSaveRequest = Schema.Struct({
  definition: Schema.optional(BrowserTaskDefinition),
  scope: Schema.optional(BrowserTaskScope),
  status: Schema.optional(BrowserTaskStatus),
  expectedVersion: NonNegativeInt,
});
export type BrowserTaskSaveRequest = typeof BrowserTaskSaveRequest.Type;
